import React from 'react';
import { Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';

import Dashboard from './pages/Dashboard';
import LandingPage from './pages/LandingPage';
import Loading from './components/Loading/Loading';
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';
import AuthenticatedLayout from './AuthenticatedLayout';
import AdminDashboard from './pages/AdminDashboard';
import AdminRoute from './components/AdminRoute/AdminRoute';
import UserManagementPage from './pages/UserManagementPage';
import RoleManagementPage from './pages/RoleManagementPage';

const App: React.FC = () => {
  const { isLoading, error: sdkError, isAuthenticated, logout } = useAuth0();
  const [searchParams] = useSearchParams();

  const urlError = searchParams.get('error');
  const urlErrorDescription = searchParams.get('error_description');

  let displayError = sdkError;

  // The Auth0 SDK might return a generic "Invalid state" error when a transaction is missing.
  // We can provide a better error message by parsing the URL query parameters directly.
  if (urlError && urlErrorDescription) {
    displayError = new Error(urlErrorDescription);
    displayError.name = urlError;
  }

  if (displayError) {
    return (
      <div className="container text-center mt-5">
        <div className="alert alert-danger" role="alert">
          <h2>{displayError.name === 'access_denied' ? 'Access Denied' : 'An Error Occurred'}</h2>
          <p>{displayError.message}</p>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
        >
          Logout and Return to Login
        </button>
      </div>
    );
  }

  if (isLoading) {
    return <Loading />;
  }

  return (
    <div id="app" className="d-flex flex-column h-100">
      <Routes>
        <Route
          path="/"
          element={!isAuthenticated ? <LandingPage /> : <Navigate to="/dashboard" />}
        />
        <Route
          path="/dashboard"
          element={<ProtectedRoute component={AuthenticatedLayout} />}
        >
          <Route index element={<Dashboard />} />
          <Route
            path="admin"
            element={<AdminRoute><AdminDashboard /></AdminRoute>}
          />
          <Route
            path="users"
            element={<AdminRoute><UserManagementPage /></AdminRoute>}
          />
          <Route
            path="roles"
            element={<AdminRoute><RoleManagementPage /></AdminRoute>}
          />
        </Route>
      </Routes>
    </div>
  );
};

export default App;
